import * as Sqrl from 'squirrelly';

import {a11y} from '../vendor/theme-scripts/theme-a11y';
import {getSizedImageUrl} from '../vendor/theme-scripts/theme-images';
import debounce from '../util/debounce';
import bgset from '../util/bgset';
import FetchError from '../util/fetch-error';
import {fetchProduct} from '../util/fetch-product';
import {formatPrices} from '../util/format-prices';

const selectors = {
  body: 'body',
  header: '[data-site-header]',
  search: '[data-nav-search]',
  searchOpen: '[data-nav-search-open]',
  searchClose: '[data-nav-search-close]',
  searchForm: '[data-nav-search-form]',
  searchContainer: '[data-nav-search-container]',
  searchScroller: '[data-nav-search-scroller]',
  searchResultsContainer: '[data-nav-search-results]',
  searchInput: '[data-nav-search-input]',
  popularSearchLink: '[data-popular-search-link]',
  productTemplate: '[product-grid-item-template]',
  productsWrapper: '[data-products-wrap]',
  noresultTemplate: '[noresult-item-template]',
  resultsPagination: '[data-results-pagination]',
  navItem: '[data-nav-item]',
};

const classes = {
  pushUp: 'push-up',
  cartDrawerOpen: 'js-drawer-open-cart',
  drawerOpen: 'js-drawer-open',
  isSearching: 'is-searching',
  isSearchVisible: 'is-search-visible',
  navSearchIsVisible: 'nav-search--is-visible',
  loading: 'loading',
  isPaginationVisible: 'is-pagination-visible',
  isVisible: 'is-visible',
  noOutline: 'no-outline',
};

const attributes = {
  ariaExpanded: 'aria-expanded',
};

let sections = {};

class NavSearch {
  constructor(container) {
    this.container = container;
    this.searchInput = this.container.querySelector(selectors.searchInput);
    this.searchClose = this.container.querySelector(selectors.searchClose);
    this.searchContainer = this.container.querySelector(selectors.searchContainer);
    this.popularLinks = this.container.querySelectorAll(selectors.popularSearchLink);
    this.scrollableElement = this.container.querySelector(selectors.searchScroller);
    this.searchResultsContainer = this.container.querySelector(selectors.searchResultsContainer);
    this.searchForm = this.container.querySelector(selectors.searchForm);
    this.resultsPagination = this.container.querySelector(selectors.resultsPagination);

    this.productTemplate = document.querySelector(selectors.productTemplate).innerHTML;
    this.productsWrapper = document.querySelector(selectors.productsWrapper);
    this.noresultTemplate = document.querySelector(selectors.noresultTemplate).innerHTML;

    this.searchOpen = document.querySelectorAll(selectors.searchOpen);
    this.bodySelector = document.querySelector(selectors.body);
    this.headerSelector = document.querySelector(selectors.header);

    this.result = null;
    this.openSearchTimeout = 0;
    this.linkOpened = null;
    this.a11y = a11y;

    this.init();
  }

  init() {
    this.initListeners();
    this.initSearch();
  }

  initSearch() {
    this.searchInput.addEventListener(
      'input',
      debounce((event) => {
        const val = event.target.value;
        if (val && val.length > 1) {
          this.searchContainer.classList.add(classes.isSearching, classes.pushUp);
          this.fetchProductSuggestions(val);
        } else {
          this.reset();
        }
      }, 300)
    );
    this.searchInput.addEventListener('clear', this.reset.bind(this));
  }

  initListeners() {
    this.container.addEventListener('keyup', (event) => {
      if (event.code === theme.keyboardKeys.ESCAPE) {
        event.stopImmediatePropagation();
        this.close();
      }
    });

    this.searchOpen.forEach((searchButton) => {
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
        this.linkOpened = event.target;
      });
    });

    this.searchClose.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });

    this.popularLinks.forEach((popularLink) => {
      popularLink.addEventListener('click', (event) => {
        event.preventDefault();
        const searchText = event.target.textContent;
        this.searchInput.value = searchText;
        window.location.href = `${theme.routes.search_url}?type=product&q=${searchText}&options[prefix]=last`;
      });
    });

    return this.linkOpened;
  }

  open() {
    const isNavDrawerOpen = this.bodySelector.classList.contains(classes.drawerOpen);
    const isCartDrawerOpen = this.bodySelector.classList.contains(classes.cartDrawerOpen);

    if (isNavDrawerOpen || isCartDrawerOpen) {
      if (typeof window.drawer.close === 'function') {
        window.drawer.close();
      }

      if (this.openSearchTimeout) {
        clearTimeout(this.openSearchTimeout);
      }
      this.openSearchTimeout = setTimeout(this.openSearch.bind(this), 400);
    } else {
      this.openSearch();
    }
  }

  openSearch() {
    const activeNavItem = this.headerSelector.querySelector(`.${classes.isVisible}${selectors.navItem}`);

    this.headerSelector.classList.add(classes.isSearchVisible);
    this.container.classList.add(classes.navSearchIsVisible);
    this.container.setAttribute(attributes.ariaExpanded, true);

    document.dispatchEvent(new CustomEvent('theme:search:open', {bubbles: true}));

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.scrollableElement}));

    setTimeout(() => {
      // Fixes hover issues on touch devices
      if (activeNavItem) {
        activeNavItem.dispatchEvent(new Event('mouseleave', {bubbles: true}));
      }

      this.a11y.trapFocus({
        container: this.container,
        elementToFocus: this.searchInput,
      });
    }, 100);
  }

  close() {
    const isSearchVisible = this.headerSelector.classList.contains(classes.isSearchVisible);
    const isFocusEnabled = !document.body.classList.contains(classes.noOutline);

    if (!isSearchVisible) {
      return;
    }

    this.reset();

    this.headerSelector.classList.remove(classes.isSearchVisible);
    this.container.classList.remove(classes.navSearchIsVisible);
    this.container.setAttribute(attributes.ariaExpanded, false);

    // Hook for navSearch close event
    document.dispatchEvent(new CustomEvent('theme:search:close', {bubbles: true}));

    if (isFocusEnabled) {
      this.a11y.removeTrapFocus({
        container: this.container,
      });

      setTimeout(() => {
        this.a11y.trapFocus({
          container: this.headerSelector,
          elementToFocus: this.linkOpened,
        });
      }, 100);
    }

    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }, 250);
  }

  reset(clearTerms = true) {
    this.productsWrapper.innerHTML = '';
    this.searchInput.val = '';
    this.a11y.removeTrapFocus();

    if (clearTerms) {
      this.searchContainer.classList.remove([classes.isSearching, classes.pushUp]);
      this.searchResultsContainer.classList.remove(classes.isPaginationVisible);
    }
  }

  fetchProductSuggestions(terms) {
    fetch(`/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`)
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.result = response.resources.results;

        return this.fetchProducts(this.result.products);
      })
      .then((response) => {
        this.productsWrapper.innerHTML = '';
        this.searchResultsContainer.classList.remove(classes.isPaginationVisible);

        if (response === '') {
          this.injectHTML(this.productsWrapper, this.renderNoResult());
        } else {
          this.injectHTML(this.productsWrapper, response);

          if (this.result.products.length > 9) {
            this.resultsPagination.getElementsByTagName('a')[0].href = `${theme.routes.search_url}?type=product&q=${terms}&options[prefix]=last`;
            this.searchResultsContainer.classList.add(classes.isPaginationVisible);
          }

          this.a11y.removeTrapFocus({
            container: this.container,
          });
          this.a11y.trapFocus({
            container: this.container,
            elementToFocus: this.searchInput,
          });
        }

        this.searchContainer.classList.remove(classes.isSearching);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  injectHTML(target, pageHTML) {
    target.innerHTML += pageHTML;
  }

  renderNoResult() {
    const text = theme.strings.no_results;

    const updateValues = {
      text,
    };

    return Sqrl.render(this.noresultTemplate, {item: updateValues});
  }

  fetchProducts(products) {
    const promises = [];
    products.forEach((product) => {
      // because of a translation bug in the predictive search API
      // we need to fetch the product JSON from the handle
      promises.push(
        fetchProduct(product.handle).then((productJSON) => {
          const formatted = formatPrices(productJSON);
          return this.renderProduct(formatted);
        })
      );
    });

    return Promise.all(promises).then((result) => {
      let str = '';
      result.forEach((render) => {
        str += render;
      });
      return str;
    });
  }

  renderProduct(product) {
    const stripHtmlRegex = /(<([^>]+)>)/gi;
    const title = product.title.replace(stripHtmlRegex, '');
    let media = null;
    let image = '';

    if (product.media !== undefined) {
      media = product.media[0];
    }

    if (media) {
      let layout = false;

      if (theme.settings.gridImageSize == 'contain') {
        layout = media.preview_image.aspect_ratio > theme.settings.gridImageAspectRatio ? 'landscape' : 'portrait';
      }

      image = {
        thumb: bgset(getSizedImageUrl(media.preview_image.src, '2048x'), media.preview_image.aspect_ratio),
        aspectRatio: media.preview_image.aspect_ratio,
        layout: layout,
      };
    } else {
      image = {
        thumb: theme.assets.no_image,
        alt: '',
        aspectRatio: 1,
      };
    }

    const updateValues = {
      ...product,
      title,
      image,
    };

    return Sqrl.render(this.productTemplate, {product: updateValues});
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

const navSearch = {
  onLoad() {
    sections[this.id] = [];
    const navSearchs = document.querySelectorAll(selectors.search);

    navSearchs.forEach((search) => {
      sections[this.id].push(new NavSearch(search));
    });
  },
};

export {navSearch};
